import { useState, useEffect } from "react";
import { api, API } from "@/App";
import { Puzzle, Download, ExternalLink, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MANIFEST_URL = `${BACKEND}/api/addin/manifest.xml`;

function Step({ n, title, children, done }) {
  return (
    <div className="flex gap-4">
      <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border ${done ? "bg-emerald-500 border-emerald-500 text-[#0a0e1c]" : "bg-white/[0.04] border-white/15 text-slate-300"}`}>
        {done ? <CheckCircle2 size={16} /> : n}
      </div>
      <div className="flex-1 pb-6 border-b border-white/5 last:border-b-0">
        <div className="font-semibold text-slate-100">{title}</div>
        <div className="text-sm text-slate-300 mt-2 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export default function Addin() {
  const [usage, setUsage] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data));
    api.get("/templates").then(r => setTemplates(r.data));
  }, []);

  const activeTemplate = templates.find(t => t.id === settings?.activeTemplateId);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
          <Puzzle size={14} /> Outlook Add-in
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Outlook Add-in (Recommended)</h1>
        <p className="text-slate-400 mt-1 text-sm max-w-2xl">
          The same approach Xink, Exclaimer & CodeTwo use: a tiny Office.js extension that runs inside Outlook itself and inserts the signature directly into the compose window — on web, iPhone, desktop, everywhere. One-time install via Microsoft 365 Admin Center.
        </p>
      </div>

      {!activeTemplate && (
        <div className="glass p-4 mb-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={18} />
          <div>
            <div className="font-medium text-amber-200">No active template set</div>
            <div className="text-xs text-slate-400 mt-0.5">The add-in needs an active template to know what HTML to insert. Set one in <strong>Brand Settings</strong> first.</div>
          </div>
        </div>
      )}

      <div className="glass p-6">
        <Step n={1} title="Download the manifest file">
          <p className="mb-3">The manifest is a small XML file that tells Microsoft 365 about the add-in (the JavaScript stays on our server — nothing is installed on user machines).</p>
          <div className="flex gap-2 flex-wrap">
            <a
              href={MANIFEST_URL}
              download="SignatureManager-manifest.xml"
              className="btn btn-primary"
              data-testid="download-manifest"
            >
              <Download size={14} /> Download manifest.xml
            </a>
            <button onClick={() => copy(MANIFEST_URL)} className="btn" data-testid="copy-manifest-url">
              <Copy size={14} /> Copy URL
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2 font-mono break-all">{MANIFEST_URL}</div>
        </Step>

        <Step n={2} title="Open Microsoft 365 Admin Center → Integrated apps">
          <p className="mb-3">Go to the Integrated apps section as a Global Admin:</p>
          <a
            href="https://admin.microsoft.com/Adminportal/Home#/Settings/IntegratedApps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
            data-testid="open-admin-center"
          >
            <ExternalLink size={14} /> Open Integrated apps
          </a>
        </Step>

        <Step n={3} title='Click "Upload custom apps"'>
          <p>Top of the page → <strong>Upload custom apps</strong> → choose <strong>Office Add-in</strong> → <strong>Upload manifest file (.xml) from device</strong> → select the file you just downloaded.</p>
        </Step>

        <Step n={4} title="Assign to users">
          <p>On the deployment screen, choose <strong>Entire organization</strong> (or select your 7 users individually). Click <strong>Next</strong> → <strong>Finish deployment</strong>.</p>
          <div className="text-[11px] text-slate-500 mt-2">Permission requested: <code className="text-emerald-300">Read/write mailbox (current user only)</code>. Microsoft will show a consent screen — accept.</div>
        </Step>

        <Step n={5} title="Wait 6–24 hours, then test">
          <p className="mb-2">Microsoft propagates the add-in to all client apps over 6–24 hours (often much faster — 5–30 min in practice). Once live:</p>
          <ul className="list-disc ml-5 space-y-1 text-sm">
            <li>Open Outlook (web, desktop or iPhone), click <strong>New mail</strong> or <strong>Reply</strong></li>
            <li>The signature auto-appears in the compose window 🎉</li>
            <li>You'll also see a new <strong>Insert signature</strong> button in the compose ribbon for manual re-insertion</li>
            <li>For replies, the signature sits above the quoted thread — under your reply text</li>
          </ul>
          <div className="text-[11px] text-slate-500 mt-2">Tip: if it doesn&apos;t appear immediately, close &amp; reopen Outlook once after deployment goes live.</div>
        </Step>

        <Step n={6} title="(Optional) Remove the transport rules">
          <p>Once you've confirmed the add-in works, you can remove the server-side transport rules to avoid double signatures:</p>
          <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm">
            <li>Go to <strong>Users &amp; Deployment</strong> tab</li>
            <li>Switch mode to <strong>Server Rule</strong></li>
            <li>Click the <strong>Remove all rules</strong> button in the amber banner (or click the trash icon per user)</li>
          </ol>
        </Step>
      </div>

      <div className="glass p-4 mt-6 border border-cyan-500/20 bg-cyan-500/[0.04]">
        <div className="font-semibold text-cyan-200 mb-2">How it works under the hood</div>
        <div className="text-xs text-slate-400 space-y-1.5 leading-relaxed">
          <div>• The add-in registers an <code className="text-cyan-300">OnNewMessageCompose</code> event handler with Outlook.</div>
          <div>• When a user clicks New / Reply / Forward, Outlook invokes the handler.</div>
          <div>• The handler calls <code className="text-cyan-300">{`${BACKEND}/api/addin/signature?email=<user>`}</code>, which returns the personalized HTML from your active template (using each user's Microsoft 365 profile data: name, job title, phone, etc.).</div>
          <div>• The handler inserts the HTML using <code className="text-cyan-300">item.body.setSignatureAsync()</code> — Outlook's official API for placing a signature, so it lands in the correct position for new emails AND replies.</div>
          <div>• <code className="text-cyan-300">disableClientSignatureAsync()</code> is called first to suppress any per-device signature users may have set locally, ensuring there's only one signature.</div>
        </div>
      </div>
    </div>
  );
}
